/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextarea } from "primeng/inputtextarea";
import { MessageService } from "primeng/api";

import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { ProcessingCycleEditRequest } from "../../interfaces/processing-cycle.interface";

/**
 * Processing Cycle Edit Dialog
 * Allows editing a processing cycle's charged total and notes
 * Matches old QMC EditCyclePopup functionality
 *
 * Supports dual mode:
 * - Standard mode: Uses SessionService for authentication
 * - Embedded mode: Uses inputToken passed via config.data
 */
@Component({
  selector: "app-processing-cycle-edit-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    InputTextarea,
  ],
  template: `
    <div class="edit-dialog">
      <div class="dialog-content">
        <!-- Charged Total -->
        <div class="field">
          <label for="chargedTotal" class="block mb-2 font-semibold">
            Charged Total <span class="text-red-500">*</span>
          </label>
          <p-inputNumber
            id="chargedTotal"
            [(ngModel)]="chargedTotal"
            mode="currency"
            currency="USD"
            locale="en-US"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            styleClass="w-full"
            [required]="true"
          />
        </div>

        <!-- Notes -->
        <div class="field">
          <label for="notes" class="block mb-2 font-semibold"> Notes </label>
          <textarea
            id="notes"
            pInputTextarea
            [(ngModel)]="notes"
            rows="4"
            class="w-full"
            placeholder="Add or update notes about this processing cycle..."
          ></textarea>
        </div>
      </div>

      <!-- Footer -->
      <div class="dialog-footer">
        <button
          pButton
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (click)="onCancel()"
          [disabled]="isLoading"
        ></button>
        <button
          pButton
          label="Save Changes"
          (click)="onSave()"
          [loading]="isLoading"
          [disabled]="!canSave()"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .edit-dialog {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .dialog-content {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .field {
        display: flex;
        flex-direction: column;
      }

      .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding-top: 1rem;
        border-top: 1px solid var(--surface-border);
      }

      .text-red-500 {
        color: var(--red-500);
      }
    `,
  ],
})
export class ProcessingCycleEditDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  siteId: number = 0;
  processingCycleId: number = 0;
  chargedTotal: number = 0;
  notes: string = "";
  inputToken?: QPilotAccessToken;
  isLoading = false;

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Get data from config
    this.siteId = this.config.data?.siteId ?? 0;
    this.processingCycleId = this.config.data?.processingCycleId ?? 0;
    this.chargedTotal = this.config.data?.chargedTotal ?? 0;
    this.notes = this.config.data?.notes ?? "";
    this.inputToken = this.config.data?.inputToken;
  }

  /**
   * Get access token based on mode
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Check if can save
   */
  canSave(): boolean {
    return this.chargedTotal > 0;
  }

  /**
   * Cancel dialog
   */
  onCancel(): void {
    this.ref.close();
  }

  /**
   * Save changes
   */
  onSave(): void {
    const accessToken = this.getAccessToken();
    if (!this.canSave() || !accessToken) {
      return;
    }

    const request: ProcessingCycleEditRequest = {
      ChargedTotal: this.chargedTotal,
      Notes: this.notes,
    };

    this.isLoading = true;

    this.qpilotService
      .editProcessingCycle(
        this.siteId,
        this.processingCycleId,
        request,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.ref.close(request);
        },
        error: (error) => {
          console.error("Failed to edit cycle:", error);
          this.isLoading = false;
          this.showError("Failed to update processing cycle");
        },
      });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
